import { MetricName } from '@canvas/frontend-metrics';

import { defineMetric, type MetricMetadataBase } from '../define-metric';
import type { FrameState } from '../stabilized-scheduler';

function collectEntryColumnCheckboxInputs(tbody: HTMLElement): HTMLInputElement[] {
  return Array.from(tbody.querySelectorAll('input[type="checkbox"]')).filter(
    (el) => el.closest('[data-entry-id]') !== null
  ) as HTMLInputElement[];
}

function findEntryCheckboxInput(
  tbody: HTMLElement,
  entryId: string
): HTMLInputElement | null {
  return tbody.querySelector<HTMLInputElement>(
    `[data-entry-id="${entryId}"] input[type="checkbox"]`
  );
}

export function getDomSingleEntryRowCheckboxMatchState(
  tableRoot: HTMLElement,
  entryId: string,
  expectChecked: boolean
): { found: boolean; matches: boolean } {
  const tbody = tableRoot.querySelector('tbody');

  if (!tbody) {
    return { found: false, matches: false };
  }

  const input = findEntryCheckboxInput(tbody, entryId);

  if (!input) {
    return { found: false, matches: false };
  }

  return { found: true, matches: input.checked === expectChecked };
}

export function singleEntryRowCheckboxFrameState(
  tableRoot: HTMLElement,
  entryId: string,
  expectChecked: boolean
): FrameState {
  const { found, matches } = getDomSingleEntryRowCheckboxMatchState(
    tableRoot,
    entryId,
    expectChecked
  );

  if (!found || !matches) {
    return { done: false, stabilityKey: null };
  }

  return { done: true, stabilityKey: entryId };
}

export function getDomRowCheckboxesSelectionSetMatchState(
  tableRoot: HTMLElement,
  expectedSelectedEntryIds: ReadonlySet<string>
): { inputCount: number; allMatch: boolean } {
  const tbody = tableRoot.querySelector('tbody');

  if (!tbody) {
    return { inputCount: 0, allMatch: false };
  }

  const inputs = collectEntryColumnCheckboxInputs(tbody);

  if (inputs.length === 0) {
    return { inputCount: 0, allMatch: false };
  }

  const allMatch = inputs.every((input) => {
    const entryId = input.closest('[data-entry-id]')?.getAttribute('data-entry-id');

    if (!entryId) {
      return false;
    }

    return input.checked === expectedSelectedEntryIds.has(entryId);
  });

  return { inputCount: inputs.length, allMatch };
}

export function rowCheckboxesSelectionSetFrameState(
  tableRoot: HTMLElement,
  expectedSelectedEntryIds: ReadonlySet<string>
): FrameState {
  const { inputCount, allMatch } = getDomRowCheckboxesSelectionSetMatchState(
    tableRoot,
    expectedSelectedEntryIds
  );

  if (inputCount === 0 || !allMatch) {
    return { done: false, stabilityKey: null };
  }

  return { done: true, stabilityKey: String(inputCount) };
}

type SingleRowProbe = {
  kind: 'single-row';
  entryId: string;
  expectRowCheckboxChecked: boolean;
};

type SelectionSetProbe = {
  kind: 'selection-set';
  expectedSelectedEntryIds: ReadonlySet<string>;
};

export interface EntryCheckboxData {
  tableRoot: HTMLElement | null;
  probe: SingleRowProbe | SelectionSetProbe;
}

export interface EntryCheckboxMetadata extends MetricMetadataBase {
  isShiftRangeSelect: boolean;
}

function buildEntryCheckboxProbe(probeData: EntryCheckboxData): () => FrameState {
  if (!probeData.tableRoot) {
    return () => ({ done: false, stabilityKey: null });
  }

  const tableRoot = probeData.tableRoot;

  if (probeData.probe.kind === 'single-row') {
    const { entryId, expectRowCheckboxChecked } = probeData.probe;
    return () =>
      singleEntryRowCheckboxFrameState(tableRoot, entryId, expectRowCheckboxChecked);
  }

  const { expectedSelectedEntryIds } = probeData.probe;
  return () => rowCheckboxesSelectionSetFrameState(tableRoot, expectedSelectedEntryIds);
}

export const entryCheckboxMetric = defineMetric<EntryCheckboxData, EntryCheckboxMetadata>(
  {
    metricName: MetricName.DATABASE_ROW_CHECKBOX_INTERACTION_TIME,
    buildProbe: buildEntryCheckboxProbe,
    buildMetadata: (metadata) => ({
      interactionTimingSource: 'dom-entry-row-checkbox-sync',
      isShiftRangeSelect: metadata.isShiftRangeSelect
    })
  }
);
